/**
 * Single registry of every visible "project" (page or tab) in the visualizer plus
 * the artifact(s) it reads. This is the canonical structure of the app.
 *
 * Two consumers:
 *   1. /pipeline Projects tab — catalog with last-updated badge per project
 *   2. The actual pages — tabs whose ids + names match this registry
 *
 * Same vocabulary in both places. Click a row in /pipeline → routes to
 * `${href}?tab=${id}` and the page selects that tab.
 *
 * RULES:
 *   - every entry must lead somewhere (a route + optional ?tab=)
 *   - every entry must reflect what the page actually renders
 *   - no orphans: if a registry entry has no UI to back it, delete it
 *   - drift between this file and the actual JSX is THE bug — keep them in sync
 */

import { BASKET_SIZE } from './alpha-basket';

export type SurfaceKind =
  | 'page'        // top-level route with no internal tabs (whole route IS the surface)
  | 'tab'         // tab within a multi-tab page; ?tab=<id> selects it
  | 'export';     // downloadable artifact, no in-app surface (shown as a separate row)

export interface Surface {
  /** Stable id; also the value of ?tab=<id> when href is a tabbed page. */
  id: string;
  /** Long descriptive label — used in /pipeline catalog. */
  label: string;
  /** Short label used in tab pills (≤ 16 chars when possible). */
  tabLabel: string;
  kind: SurfaceKind;
  /** Logical group / parent page route. For tabs, the page that hosts the tab bar. */
  href?: string;
  /**
   * Optional override for the actual URL this tab lives at. When unset, defaults to
   * `${href}?tab=${id}` (in-page tab). Set when a tab's content lives at its own route
   * (e.g. /research-desk/leaderboard) — clicking the pill navigates there.
   */
  route?: string;
  /** Repo-relative paths (or absolute for cross-project artifacts). Newest mtime wins. */
  artifacts: string[];
  /** Optional one-liner shown under the tab label / in /pipeline. */
  notes?: string;
  /**
   * Optional shell command that re-runs whatever produces this surface's data. When set,
   * the /pipeline → Projects row's status glyph becomes clickable: click → spawn this
   * command via /api/pipeline-refresh?surfaceId=<id>. Whitelisted to commands declared
   * HERE — never accept user-supplied commands. Run from the visualizer repo root.
   */
  refreshCommand?: string;
  /** Optional human-readable prerequisite shown in the confirm dialog before refresh runs. */
  refreshNote?: string;
  /**
   * Days of staleness allowed before the status flips to "stale". Default is 1 (anything
   * with asOf older than today). Set higher for surfaces with longer cadence — e.g. Live
   * Tracker is monthly so it shouldn't trip stale until ~35 days old.
   */
  staleAfterDays?: number;
}

export const DATA_SURFACES: Surface[] = [
  // ─── Visualizer (/) ────────────────────────────────────────────────────
  {
    id: 'visualizer-3d',
    label: '3D scatter (Quality × Valuation × Momentum) — every stock in the universe',
    tabLabel: '3D Scatter',
    kind: 'page',
    href: '/',
    artifacts: ['data/scores.json'],
  },

  // ─── Portfolio (/portfolio) — 4 tabs ──────────────────────────────────
  {
    id: 'overview',
    label: 'Portfolio Overview · snapshot · analytics · sector mix · holdings',
    tabLabel: 'Overview',
    kind: 'tab',
    href: '/portfolio',
    artifacts: ['data/scores.json', 'data/portfolio.json', 'data/portfolio-snapshot.json'],
  },
  {
    id: 'scorecard',
    label: 'Portfolio Scorecard · deck-style 10-column factor grid with live weights',
    tabLabel: 'Scorecard',
    kind: 'tab',
    href: '/portfolio',
    artifacts: ['data/scores.json', 'data/portfolio.json'],
  },
  {
    id: 'history',
    label: 'Portfolio History · per-holding composite trajectory across 1w / 1m / 3m / 6m / 1y',
    tabLabel: 'History',
    kind: 'tab',
    href: '/portfolio',
    artifacts: ['data/portfolio-snapshot.json'],
  },
  {
    id: 'earnings',
    label: 'Portfolio Earnings · upcoming reports · post-report impact · AM brief',
    tabLabel: 'Earnings',
    kind: 'tab',
    href: '/portfolio',
    artifacts: ['data/upcoming-earnings.json', 'data/earnings-impact.json', 'data/research/am-report-latest.json'],
  },
  {
    id: 'risk',
    label: 'Portfolio Risk · drawdown · correlation · risk decomposition · rebalance simulator',
    tabLabel: 'Risk',
    kind: 'tab',
    href: '/portfolio',
    artifacts: ['data/portfolio-snapshot.json'],
  },

  // ─── Alpha Zone (/alpha-zone) — 4 tabs mirroring /portfolio (Steve directive 2026-05-07).
  {
    id: 'overview',
    label: 'Alpha Zone Overview · headline performance, visualizer, basket holdings',
    tabLabel: 'Overview',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket.json', 'data/alpha-basket-snapshot.json'],
  },
  {
    id: 'scorecard',
    label: `Alpha Zone Scorecard · ${BASKET_SIZE} names by factor scores`,
    tabLabel: 'Scorecard',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket.json', 'data/scores.json'],
  },
  {
    id: 'history',
    label: 'Alpha Zone History · per-basket-member composite trajectory across 1w / 1m / 3m / 6m / 1y',
    tabLabel: 'History',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket.json', 'data/portfolio-snapshot.json'],
  },
  {
    id: 'earnings',
    label: 'Alpha Zone Earnings · upcoming + recent for the basket',
    tabLabel: 'Earnings',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket.json', 'data/upcoming-earnings.json'],
  },
  {
    id: 'risk',
    label: 'Alpha Zone Risk · drawdown, correlation, rebalance simulator',
    tabLabel: 'Risk',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket-snapshot.json'],
  },
  {
    id: 'leaders',
    label: 'Sector Leaders · top 3 by composite within each peer group (standard / financial / insurance / energy / utility / REIT). Q ≥ 0 gated. Read-only lens — not a basket.',
    tabLabel: 'Sector Leaders',
    kind: 'tab',
    href: '/alpha-zone',
    artifacts: ['data/scores.json'],
  },

  // ─── Alpha Zone (/alpha-zone) — top-level surface (split out 2026-05-06).
  // Was previously a tab under /research-desk; now its own page so it's the
  // first thing users see and isn't lumped with research workflows.
  {
    id: 'alpha-zone',
    label: 'Alpha Zone · live performance of the top-20 model basket vs SPY · monthly rebalanced',
    tabLabel: 'Alpha Zone',
    kind: 'page',
    href: '/alpha-zone',
    artifacts: ['data/alpha-basket.json', 'data/live-tracker/snapshots'],
  },

  // ─── Research Desk (/research-desk) — tabs ─────────────────────────
  {
    id: 'today',
    label: 'Ideas · open + closed recommendations from the desk, with movers feed',
    tabLabel: 'Ideas',
    kind: 'tab',
    href: '/research-desk',
    artifacts: [
      'data/research-notes.json',
      'data/score-velocity.json',
      'data/portfolio-fit.json',
    ],
  },
  {
    id: 'movers',
    label: 'Movers · 5 ranked cross-scope lists: composite movers, Congressional top buys/sells, EPS revisions, transcript tones',
    tabLabel: 'Movers',
    kind: 'tab',
    href: '/research-desk',
    artifacts: [
      'data/scores.json',
      'data/score-velocity.json',
      'data/congressional-cache.json',
      'data/transcripts-cache.json',
    ],
  },
  {
    id: 'alpha-reports',
    label: 'Alpha Zone Reports · a full research note on each Alpha Zone holding (business, composite breakdown, factor leaves, valuation, target)',
    tabLabel: 'Alpha Reports',
    kind: 'tab',
    href: '/research-desk',
    artifacts: ['data/alpha-zone-reports.json'],
  },

  // ─── Leaderboard — tab of /research-desk, route /research-desk/leaderboard
  {
    id: 'leaderboard',
    label: 'Leaderboard · Alpha/Beta zone changes · top movers by Composite/Quality/Valuation',
    tabLabel: 'Leaderboard',
    kind: 'tab',
    href: '/research-desk',
    route: '/research-desk/leaderboard',
    artifacts: ['data/leaderboard.json', 'data/scores.json'],
  },

  // ─── Peer Group Trends — tab of /research-desk ──────────────────────
  {
    id: 'peer-group-trends',
    label: 'Peer Group Trends · mean ± 1σ of Quality and Valuation scores for each peer group over time',
    tabLabel: 'Group Trends',
    kind: 'tab',
    href: '/research-desk',
    artifacts: ['data/composite-monthly-history.json', 'data/scores.json'],
  },

  // ─── History Lab — tab of /research-desk, route /research-desk/history-lab
  {
    id: 'history-lab',
    label: 'Universe Regime Lab · SPX vs own 27-yr history (16 BBG fundamentals)',
    tabLabel: 'History Lab',
    kind: 'tab',
    href: '/research-desk',
    route: '/research-desk/history-lab',
    artifacts: ['data/history/universe-regime.json', 'data/history/spx-fundamentals.json'],
    notes: 'SPX index history requires logged-in Bloomberg terminal — manual refresh.',
    refreshCommand: 'BBG_SAPI_HOST=stevenewlaptop BBG_SAPI_PORT=8294 python3 sapi/scripts/fetch-spx-history.py && npx tsx scripts/research/history/compute-universe-regime.ts',
    refreshNote: 'Requires Bloomberg terminal logged in at 10.0.1.120 (USER auth). APP-only fallback returns 0 obs and the write-guard preserves yesterday\'s data.',
  },

  // ─── Live Tracker (/live-tracker) ────────────────────────────────────
  {
    id: 'live-tracker',
    label: 'Live Performance Tracker · monthly Alpha+Beta basket vs SPY',
    tabLabel: 'Live Tracker',
    kind: 'page',
    href: '/live-tracker',
    artifacts: ['data/live-tracker'],
    notes: 'Monthly cadence: 1st captures snapshot, 5th computes returns.',
    staleAfterDays: 35, // Monthly cadence — don't flag stale until past one month boundary.
    refreshCommand: 'npx tsx scripts/live-tracker/snapshot-live-tracker.ts && npx tsx scripts/live-tracker/compute-live-tracker.ts',
    refreshNote: 'Captures the current Alpha+Beta basket and SPY/SPX benchmarks, then recomputes monthly returns. Safe to run any day.',
  },

  // ─── Presentation (/presentation) ────────────────────────────────────
  {
    id: 'presentation',
    label: 'Presentation · 15-slide live deck mirroring the investor PDF',
    tabLabel: 'Presentation',
    kind: 'page',
    href: '/presentation',
    artifacts: ['data/scores.json', 'data/portfolio.json', 'data/portfolio-snapshot.json'],
  },

  // ─── Pipeline (/pipeline) — 4 tabs (restructured 2026-05-21) ─────────
  // Sub-tab count went 7 → 4: DATA / FACTORS / VISUALIZER / PROJECTS folded
  // into ARCHITECTURE so the page reads as one INPUTS → PROCESSES → OUTPUTS
  // surface. Architecture is the default tab.
  {
    id: 'architecture',
    label: 'Architecture · the live INPUTS → PROCESSES → OUTPUTS map (default tab, sources + resolver + scoring + outputs)',
    tabLabel: 'Architecture',
    kind: 'tab',
    href: '/pipeline',
    artifacts: [
      'data/pipeline-status.jsonl',
      'data/audit-history',
      'data/build-protection.jsonl',
      'data/scores.json',
      'data/fundamentals-resolved.json',
      'data/data-completeness-audit.json',
    ],
    staleAfterDays: 1,
  },
  {
    id: 'health',
    label: 'Code Health · weekly audit (Sat 23:37): god files, fat routes, ad-hoc fetches, dated comments, dead worktrees',
    tabLabel: 'Health',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/code-health.json'],
    staleAfterDays: 8,  // weekly cadence — past 8 days = the cron didn't fire
  },
  {
    id: 'protection',
    label: 'Build Protection · day-over-day regression tracker + self-heal playbook + iMessage escalation (Steve directive 2026-05-20)',
    tabLabel: 'Protection',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/build-protection.jsonl', 'data/audit-history'],
  },
  {
    id: 'tocome',
    label: 'To-Come · candidate universe additions >$5B not yet scored, manual refresh',
    tabLabel: 'To-Come',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/universe-candidates.json'],
    refreshCommand: 'npx tsx --env-file=.env.local scripts/data-pipeline/build-universe-candidates.ts',
  },

  // ─── Hedge Lab (/hedge-lab) — added 2026-05-07 per Steve directive.
  //   Per-holding RSI(D/W/M) + momentum + click-through to live BBG option
  //   chain hedge structures (covered call / protective put / collar).
  {
    id: 'hedge-lab',
    label: 'Hedge Lab · per-holding RSI + live BBG options + suggested hedge structures',
    tabLabel: 'Hedge Lab',
    kind: 'page',
    href: '/hedge-lab',
    artifacts: ['data/options-chain.json', 'data/portfolio.json', 'data/technical.json', 'data/technical-weekly.json', 'data/technical-monthly.json'],
    staleAfterDays: 2,
  },

  // ─── Rating (/rating) ────────────────────────────────────────────────
  {
    id: 'rating',
    label: 'Rating · independent audit of the model signal, code health, and live track record',
    tabLabel: 'Rating',
    kind: 'page',
    href: '/rating',
    artifacts: [
      'data/code-health.json',
      'data/alpha-basket.json',
      'data/alpha-basket-snapshot.json',
      'data/monthly-vintage-cache.json',
    ],
    staleAfterDays: 8,
  },

  // ─── Exports (downloadable artifacts; not in-app surfaces) ────────────
  {
    id: 'website-pdf',
    label: 'Investor presentation PDF served by triumphasset.com',
    tabLabel: 'Website PDF',
    kind: 'export',
    artifacts: ['/Users/tuchnerbot/Projects/website-portal/public/triumph-presentation-latest.pdf'],
    notes: 'Daily 6 AM auto-deploy via daily-refresh.sh + Vercel.',
  },
  {
    id: 'website-model-stats',
    label: 'model-stats.json (website-portal copy)',
    tabLabel: 'Website Stats',
    kind: 'export',
    artifacts: ['/Users/tuchnerbot/Projects/website-portal/src/data/model-stats.json'],
    notes: 'Drives the live numbers (763 / 36 / pillar counts) on triumphasset.com.',
  },
  {
    id: 'website-scatter',
    label: 'scatter-data.json (website-portal copy)',
    tabLabel: 'Website Scatter',
    kind: 'export',
    artifacts: ['/Users/tuchnerbot/Projects/website-portal/public/scatter-data.json'],
    notes: 'Drives the spinning 3D scatter globe on triumphasset.com.',
  },
];
